/* ---------------------------------------------------------------------------
   What is drawn for somebody the register will not let read it.

   ONE STATE REACHES THIS, and it is a suspended account — every role carries
   `register.read`, and so does holding none. See src/data/roles.ts.

   WHY IT IS A SCREEN AND NOT A FAILED REQUEST. Without it, this application
   already had an answer for a suspended reader and the answer was a lie. Every
   `/api` read 403s, `hydrate` treats that like any other unreachable backend, and
   the fallback is the bundled seed — so somebody whose access had been withdrawn
   would be shown a register of five structures, drawn normally, with the reason
   available only in a tooltip on the connection pill. That is the same failure as
   a screening panel answering "fine" about a structure it knows nothing about: an
   answer somebody acts on, in place of a gap somebody could act on.

   So the shell draws this instead of the screen. It states the position, names
   the role, says who can change it, and offers the two things that are genuinely
   still available: the guide, which is part of this build and needs no register,
   and signing out — because the commonest reason to be looking at this is being
   signed in as the wrong person.

   WHAT IT DOES NOT DO IS GUESS. It appears only where the register has actually
   said so — `can` is false only for a role the backend reported, never merely
   because a request failed or a device has no signal. A screen that appeared
   whenever the network did not answer would tell an inspector in a gorge that
   their access had been withdrawn.
   --------------------------------------------------------------------------- */

import { h, icon, ICONS } from '../core/dom.ts';
import { actions, can, type State } from '../core/store.ts';
import type { Screen } from '../data/types.ts';
import { button, card, cardHead, fact } from '../components/widgets.ts';
import { rolesLabel } from '../data/roles.ts';
import { agency } from '../tenant/agency.ts';

/**
 * Whether this screen is replaced by the refusal above.
 *
 * A FUNCTION, AND EXPORTED, for the reason this codebase has now learned four
 * times: the rule was written inline in src/main.ts's dispatcher, and the
 * dispatcher is unreachable from a check — the smoke suite composes screens
 * through its own renderer map, precisely so that a screen added to the union
 * cannot go unrendered. A rule living inside a function no check can call is a
 * rule nothing holds. See the note on `routeFor` in src/core/router.ts, which is
 * the same fix.
 *
 * Two screens survive the refusal, and both deliberately: the guide reads nothing
 * from the register and is where the roles are explained, and Options is where
 * signing out is.
 */
export function refusesRegister(s: Readonly<State>, screen: Screen): boolean {
  return !can(s, 'register.read') && screen !== 'guide' && screen !== 'options';
}

export function suspendedScreen(s: Readonly<State>): HTMLElement {
  const roles = s.identity?.roles ?? null;

  return h(
    'div',
    { class: 'ppl' },
    h(
      'div',
      { class: 'ppl__lead' },
      h('span', { class: 'ppl__leadicon' }, icon(ICONS.people, { size: 18 })),
      h('p', {
        class: 'ppl__leadtext',
        text:
          'You are signed in, and this register is not open to you. That is a decision somebody ' +
          'in the Department made, not a fault with the application or with this device.',
      }),
    ),
    card(
      { pad: true },
      cardHead('No access to the register'),
      fact('Signed in as', s.identity?.email ?? s.identity?.author ?? '—'),
      fact('Your account', roles ? rolesLabel(roles, s.identity?.suspended) : '—'),
      h('p', {
        class: 'ppl__hint',
        text: 'A register administrator can change it from the People screen.',
      }),
      h(
        'div',
        { class: 'ppl__actions' },
        /* The guide is part of this build and reads nothing from the register, so
           it genuinely still works — and it is where the roles are explained to
           somebody who has just met one. */
        button('Open the guide', {
          primary: true,
          tall: true,
          iconPath: ICONS.book,
          title: 'What the roles are, and what each one may do',
          onClick: () => actions.go('guide'),
        }),
        /* And the likeliest actual remedy: a shared device, or a browser signed
           in as somebody else. */
        button('Sign out', {
          tall: true,
          title: 'End this browser’s session',
          onClick: () => void actions.signOut(),
        }),
      ),
    ),
    h('p', { class: 'ppl__hint', text: `${agency().department} · ${agency().branch}` }),
  );
}
